<script setup>
import { provide, computed } from 'vue'

const props = defineProps({
  label: {
    type: String,
    default: null,
  },
  subLabel: {
    type: String,
    default: null,
  },
  message: {
    type: [String, Array],
    default: null,
  },
  messageType: {
    type: String,
    default: null,
  },
  isFullwidth: {
    type: Boolean,
    default: false,
  },
  isAttached: {
    type: Boolean,
    default: false,
  },
  isRow: {
    type: Boolean,
    default: false,
  },
  isLabelBold: {
    type: Boolean,
    default: false,
  },
  labelImages: {
    type: Array,
    default: () => [],
  },
  footer: {
    type: String,
    default: null,
  },
})

const allowedMessageTypes = ['success', 'warning', 'error']

const fieldType = computed(() => allowedMessageTypes.includes(props.messageType) ? props.messageType : null)

const messages = computed(() => typeof props.message === 'string' ? [props.message] : props.message)

const hasLabels = computed(() => !!props.label || !!props.subLabel)

const hasLabelImages = computed(() => props.labelImages.length > 0)

const hasMessages = computed(() => props.message?.length > 0)

provide('fieldType', fieldType)
provide('isFullwidth', props.isFullwidth)
</script>

<template>
  <div class="base-field" :class="{ 'base-field--fullwidth': props.isFullwidth, 'base-field--row': props.isRow }">
    <div v-if="hasLabels" class="base-field__labels">
      <span v-if="props.label" class="base-field__label" :class="{ 'base-field__label--bold': props.isLabelBold }">
        {{ props.label.trim() }}
      </span>
      <span v-if="props.subLabel" class="base-field__label base-field__label--secondary">
        {{ props.subLabel.trim() }}
      </span>
      <div v-if="hasLabelImages" class="base-field__label base-field__label--images">
        <img
          v-for="imageName in props.labelImages"
          :key="imageName"
          :src="`/images/form/${imageName}.png`"
          class="base-field__label-image"
        >
      </div>
    </div>
    <div
      class="base-field__field"
      :class="{
        'base-field__field--fullwidth': props.isFullwidth,
        'base-field__field--attached': props.isAttached,
      }"
    >
      <slot />
    </div>
    <div
      v-if="hasMessages"
      class="base-field__message"
      :class="{ [`base-field__message--${fieldType}`] : !!fieldType }"
    >
      <p v-for="(fieldMessage, index) in messages" :key="index">
        {{ fieldMessage }}
      </p>
    </div>
    <div v-if="!!props.footer" class="base-field__footer">
      <p>{{ props.footer }}</p>
    </div>
  </div>
</template>

<style lang="scss">
.base-field {
  display: flex;

  flex-direction: column;
  row-gap: $spacer-half;

  width: min-content;

  &--fullwidth {
    width: auto;
  }

  &--row {
    flex-direction: row;
    gap: $spacer*1.5;
    align-items: flex-end;

    width: fit-content;
  }

  &__labels {
    display: flex;

    column-gap: $spacer;
    align-items: flex-end;
  }

  &__label {
    font-size: ms(-1);
    line-height: $font-lineheight-large;
    font-weight: $font-weight-regular;

    &--secondary {
      color: $color-neutral-dark;
    }

    &--bold {
      font-weight: $font-weight-semibold;
    }

    &--images {
      display: flex;

      gap: $spacer-half;
    }

    &:last-child:not(:only-child) {
      margin-left: auto;
    }
  }

  &__label-image {
    height: 20px;
  }

  &__field {
    display: flex;

    gap: $spacer;
    align-items: flex-start;

    min-width: max-content;
    max-height: min-content;

    // Overwrite button class to fit button with inputs.
    .button {
      align-self: stretch;

      margin: 0;
      padding-top: 0;
      padding-bottom: 0;
    }

    &--fullwidth {
      min-width: unset;

      & > * {
        min-width: unset;
      }
    }

    &--attached {
      gap: unset;

      & > *:first-child:not(:only-child) {
        margin-right: -$border-weight-hairline;

        &.select,
        &.input,
        .select,
        .input {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;

          &--hovered,
          &:hover,
          &:focus {
            z-index: z-number(base-field-hover);
          }
        }

        &.button {
          min-height: unset;

          border-top-right-radius: 0;
          border-bottom-right-radius: 0;

          &:hover {
            z-index: z-number(base-field-hover);
          }
        }
      }

      & > *:not(:first-child, :last-child) {
        margin-right: -$border-weight-hairline;

        &.select,
        &.input,
        .select,
        .input {
          border-radius: 0;

          &--hovered,
          &:hover,
          &:focus {
            z-index: z-number(base-field-hover);
          }
        }

        &.button {
          border-radius: 0;
          min-height: unset;
        }
      }

      & > *:last-child:not(:only-child) {
        &.select,
        &.input,
        .select,
        .input {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;

          &--hovered,
          &:hover,
          &:focus {
            z-index: z-number(base-field-hover);
          }
        }

        &.button {
          min-height: unset;

          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }
      }
    }
  }

  &__message {
    font-size: ms(-1);
    text-align: left;
    color: $color-neutral-dark;

    &--success {
      color: $color-feedback-success-dark;
    }

    &--warning {
      color: $color-feedback-warning-dark;
    }

    &--error {
      color: $color-feedback-error-dark;
    }
  }

  &__footer {
    font-size: ms(-1);
    line-height: $font-lineheight-large;
    font-weight: $font-weight-medium;
    color: $color-neutral-dark;
  }
}
</style>
